import {
  ApolloClient, InMemoryCache, ApolloLink, concat, split,
  Operation, NextLink, DocumentNode, FetchPolicy, ErrorPolicy,
} from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { optimizeDocumentNode, removeEmptyNodes } from '@graphql-tools/optimize';
import { createClient, Client } from 'graphql-ws';
import { createUploadLink } from 'apollo-upload-client';
import ScreenService from './screen-service';
import FormatService from './format-service';
import MainService from './main-service';
import AuthService from './auth-service';
import DateService from './date-service';
import typePolicyMap from '../graphql/type-policy-map/type-policy-map';
import { NODE_ENV_DEVELOPMENT } from '../constants/system/node-envs';
import {
  HEADER_KEY_AUTHORIZATION, HEADER_KEY_DEVICE_ID, HEADER_KEY_TIMEZONE_OFFSET, HEADER_KEY_APOLLO_REQUIRE_PREFLIGHT,
} from '../constants/system/header-keys';

export default class ClientService {
  private static KEEP_ALIVE_TIMEOUT: number = 10000;
  private static GRAPHQL_OPERATION_TYPE_SUBSCRIPTION: string = 'subscription';
  private static GRAPHQL_PATH: string = '/graphql';
  private static FETCH_POLICY_CACHE_NO_CACHE: FetchPolicy = 'no-cache';
  private static ERROR_POLICY_ERROR_NONE: ErrorPolicy = 'none';

  private static client: ApolloClient<ApolloCacheShape> | null = null;
  private static webSocketClient: Client | null = null;
  private static authToken: string | null = null;
  private static deviceId: string | null = null;

  public static async sendGraphqlQueryRequest(
    graphqlQuery: DocumentNode,
    payload: ActionPayload,
  ): Promise<ActionResponse | null> {
    const client: ApolloClient<ApolloCacheShape> = this.getClient();
    const response: ApolloClientResponse = await client.query({
      query: optimizeDocumentNode(graphqlQuery, [removeEmptyNodes]),
      variables: payload,
      context: {
        headers: this.getLocalHeaders(),
      },
    });

    return response.data ? response.data[Object.keys(response.data)[0]] : null;
  }

  public static async sendGraphqlMutationRequest(
    graphqlQuery: DocumentNode,
    payload: ActionPayload,
  ): Promise<ActionResponse | null> {
    const client: ApolloClient<ApolloCacheShape> = this.getClient();
    const response: ApolloClientResponse = await client.mutate({
      mutation: optimizeDocumentNode(graphqlQuery, [removeEmptyNodes]),
      variables: payload,
      context: {
        headers: this.getLocalHeaders(),
      },
    });

    return response.data ? response.data[Object.keys(response.data)[0]] : null;
  }

  public static async sendGraphqlSubscriptionRequest(
    graphqlSubscription: DocumentNode,
    payload: ActionPayload,
    successCallback?: Function,
    errorCallback?: Function,
  ): Promise<ApolloClientSubscription> {
    const client: ApolloClient<ApolloCacheShape> = this.getClient();

    return client.subscribe({
      query: optimizeDocumentNode(graphqlSubscription, [removeEmptyNodes]),
      variables: payload,
      context: {
        headers: this.getLocalHeaders(),
      },
    }).subscribe({
      next(response: ApolloClientResponse): void {
        if (successCallback) {
          successCallback(response.data ? response.data[Object.keys(response.data)[0]] : null);
        }
      },
      error(error: GraphQLServerError): void {
        if (errorCallback) {
          errorCallback(error);
        }
      },
    });
  }

  public static resetToken(): void {
    this.authToken = null;
  }

  public static closeConnections(): void {
    if (this.webSocketClient) {
      this.webSocketClient.dispose();
    }
  }

  private static getLocalHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};

    const isBrowser: boolean = ScreenService.checkIsBrowser();
    if (isBrowser) {
      const authToken: string | null = AuthService.getAuthToken() || this.authToken;
      if (authToken) {
        headers[HEADER_KEY_AUTHORIZATION] = authToken;
      }

      const deviceId: string | null = AuthService.getDeviceId() || this.deviceId;
      if (deviceId) {
        headers[HEADER_KEY_DEVICE_ID] = deviceId;
      }

      const timezoneOffset: number = DateService.getTimezoneOffset();
      if (timezoneOffset) {
        headers[HEADER_KEY_TIMEZONE_OFFSET] = timezoneOffset.toString();
      }
    }

    return headers;
  }

  private static getClient(): ApolloClient<ApolloCacheShape> {
    const isBrowser: boolean = ScreenService.checkIsBrowser();
    const authToken: string | null = isBrowser ? (AuthService.getAuthToken() || this.authToken) : null;
    const deviceId: string | null = isBrowser ? (AuthService.getDeviceId() || this.deviceId) : null;

    if (this.client && authToken === this.authToken && deviceId === this.deviceId) {
      return this.client;
    }

    let link: ApolloLink;
    const backendUri: string = `${MainService.BACKEND_HOST}${this.GRAPHQL_PATH}`;
    const backendWebsocketUri: string = `${MainService.BACKEND_WEBSOCKET_HOST}${this.GRAPHQL_PATH}`;

    const uploadLink: ApolloLink = createUploadLink({
      uri: backendUri,
      headers: {
        [HEADER_KEY_APOLLO_REQUIRE_PREFLIGHT]: FormatService.BOOLEAN_VALUE_TRUE,
      },
    });
    const middlewareLink: ApolloLink = new ApolloLink((
      operation: Operation,
      forward: NextLink,
    ): ApolloLinkObservable | null => (
      forward ? forward(operation) : null
    ));

    this.authToken = authToken;
    this.deviceId = deviceId;

    const headerKeyAuthorization: string = HEADER_KEY_AUTHORIZATION.toLowerCase();
    const headerKeyDeviceId: string = HEADER_KEY_DEVICE_ID.toLowerCase();

    if (this.authToken && this.deviceId) {
      this.webSocketClient = createClient({
        url: backendWebsocketUri,
        keepAlive: this.KEEP_ALIVE_TIMEOUT,
        lazy: true,
        connectionParams: {
          [headerKeyAuthorization]: this.authToken,
          [headerKeyDeviceId]: this.deviceId,
        },
      });
      const webSocketLink: GraphQLWsLink = new GraphQLWsLink(this.webSocketClient);

      const subscriptionFilter: (queryOperation: Operation) => boolean = (queryOperation: Operation): boolean => {
        const { query } = queryOperation;

        const mainDefinition: GraphqlSubscriptionDefinitionNode = getMainDefinition(query);
        const { operation } = mainDefinition;

        return operation === this.GRAPHQL_OPERATION_TYPE_SUBSCRIPTION;
      };

      link = split(subscriptionFilter, webSocketLink, concat(middlewareLink, uploadLink));
    } else {
      link = concat(middlewareLink, uploadLink);
    }

    this.client = new ApolloClient({
      link,
      cache: new InMemoryCache({
        typePolicies: typePolicyMap,
      }),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: this.FETCH_POLICY_CACHE_NO_CACHE,
          errorPolicy: this.ERROR_POLICY_ERROR_NONE,
          nextFetchPolicy: this.FETCH_POLICY_CACHE_NO_CACHE,
        },
        query: {
          fetchPolicy: this.FETCH_POLICY_CACHE_NO_CACHE,
          errorPolicy: this.ERROR_POLICY_ERROR_NONE,
        },
      },
      connectToDevTools: MainService.NODE_ENV === NODE_ENV_DEVELOPMENT,
    });

    return this.client;
  }
}
